import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queries } from '../../../queries';
import {
  ADMIN_BASE_PATH,
  claimDetailPath,
} from '../../../utils/shell-paths.utils';
import { extractClaim } from '../utils/claimNumberUtils';
import classes from './CmailDetailsPanel.module.css';

interface CmailClaimLinkProps {
  readonly extractedData?: string;
}

/**
 * "Go to <claim number>" for an email whose extraction identified a claim.
 *
 * <p>When enrichment resolved the claim, its id is in the extraction and the link uses it as is.
 * Otherwise the number is looked up, and nothing renders until it resolves: an extracted number
 * can be misread from the email, and a link to a claim that does not exist is worse than no link.
 */
export const CmailClaimLink: React.FC<CmailClaimLinkProps> = ({
  extractedData,
}) => {
  const extracted = React.useMemo(
    () => extractClaim(extractedData),
    [extractedData]
  );
  const needsLookup = Boolean(extracted && !extracted.claimId);

  const lookup = useQuery({
    ...queries.claims.byNumber(extracted?.claimNumber ?? ''),
    enabled: needsLookup,
    retry: false,
    suspense: false,
    useErrorBoundary: false,
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  let claim: { id: string; claimNumber: string } | null = null;
  if (extracted?.claimId) {
    claim = { id: extracted.claimId, claimNumber: extracted.claimNumber };
  } else if (needsLookup && lookup.data) {
    claim = lookup.data;
  }

  if (!claim) {
    return null;
  }

  return (
    <Link
      to={claimDetailPath(ADMIN_BASE_PATH, claim.id)}
      className={classes.submissionLink}
    >
      Go to <b className={classes.submissionLinkBold}>{claim.claimNumber}</b>
    </Link>
  );
};
